import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import HrMapper from '../../mappers/hr.mapper';
import {
  HrApprovalDecision,
  HrApprovalKind,
  HrApprovalsModel,
  HrAttendanceStatusModel,
  HrLeaveRequestModel,
  HrLeaveRequestPayload,
  HrLeaveTypeModel,
  HrMeModel,
  HrWfhRequestModel,
  HrWfhRequestPayload
} from '../../models/hr.model';
import {
  HrApprovalsUiModel,
  HrAttendanceUiModel,
  HrLeaveRequestUiModel,
  HrLeaveTypeUiModel,
  HrProfileUiModel,
  HrWfhRequestUiModel
} from '../../ui-models/hr.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';

/**
 * HR self-service (#699). In the platform layer because two things read it: HrModule, and the
 * side menu's check-in / check-out button, which every page renders.
 */
const MODULE_NAME = 'hr';

/** The BFF's answer when the signed-in user has no active Horilla employee. */
export const HR_NOT_LINKED = 'HR_NOT_LINKED';

type RawResponse = {
  ok?: boolean;
  status?: number;
  data?: unknown;
  problem?: unknown;
};

/**
 * The contract answers bare shapes (`{ linked }`, `[...]`); most of this BFF's own routes wrap
 * in `{ data }`. Accept either, so the two halves of #699 can land in any order. None of the
 * HR shapes has a top-level `data` key, so the check cannot misfire.
 */
export function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in (body as object)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

const errorCodeOf = (res: RawResponse) => {
  const body = (res.data || {}) as { errorCode?: string; code?: string };
  return String(body.errorCode || body.code || '');
};

const fail = (response: unknown, fn: unknown): ErrorInfo => {
  const res = response as RawResponse;
  const body = (res.data || {}) as { message?: string };
  const info = errorHelper(
    Number(res.status),
    errorCodeOf(res) || String(res.problem || ''),
    MODULE_NAME,
    (fn as { displayName?: string }).displayName as string
  );
  return body.message ? { ...info, message: String(body.message) } : info;
};

/** errorHelper puts the response's code on `code`, which ErrorInfo does not declare. */
export const isNotLinked = (error: ErrorInfo) =>
  error.status === 404 &&
  ((error as ErrorInfo & { code?: string }).code === HR_NOT_LINKED ||
    error.errorCode === HR_NOT_LINKED);

/** Who the user is in HR. Never an error for "not linked": that is `{ linked: false }`. */
export function getHrProfile(): Promise<HrProfileUiModel | ErrorInfo> {
  return api.getHrMe().then((response) => {
    if (response.ok && response.data) return HrMapper.mapProfile(unwrap<HrMeModel>(response.data));
    if (response.status === 404 && errorCodeOf(response) === HR_NOT_LINKED) {
      return HrMapper.mapProfile({ linked: false });
    }
    return fail(response, api.getHrMe);
  });
}

export function getHrLeaveTypes(): Promise<HrLeaveTypeUiModel[] | ErrorInfo> {
  return api.getHrLeaveTypes().then((response) => {
    if (response.ok) {
      return (unwrap<HrLeaveTypeModel[]>(response.data) || []).map(HrMapper.mapLeaveType);
    }
    return fail(response, api.getHrLeaveTypes);
  });
}

export function getHrLeaveRequests(): Promise<HrLeaveRequestUiModel[] | ErrorInfo> {
  return api.getHrLeaveRequests().then((response) => {
    if (response.ok) {
      return (unwrap<HrLeaveRequestModel[]>(response.data) || []).map(HrMapper.mapLeaveRequest);
    }
    return fail(response, api.getHrLeaveRequests);
  });
}

export function createHrLeaveRequest(payload: HrLeaveRequestPayload): Promise<true | ErrorInfo> {
  return api.createHrLeaveRequest(payload).then((response) => {
    if (response.ok) return true;
    return fail(response, api.createHrLeaveRequest);
  });
}

export function cancelHrLeaveRequest(id: string): Promise<true | ErrorInfo> {
  return api.cancelHrLeaveRequest(id).then((response) => {
    if (response.ok) return true;
    return fail(response, api.cancelHrLeaveRequest);
  });
}

export function getHrWfhRequests(): Promise<HrWfhRequestUiModel[] | ErrorInfo> {
  return api.getHrWfhRequests().then((response) => {
    if (response.ok) {
      return (unwrap<HrWfhRequestModel[]>(response.data) || []).map(HrMapper.mapWfhRequest);
    }
    return fail(response, api.getHrWfhRequests);
  });
}

export function createHrWfhRequest(payload: HrWfhRequestPayload): Promise<true | ErrorInfo> {
  return api.createHrWfhRequest(payload).then((response) => {
    if (response.ok) return true;
    return fail(response, api.createHrWfhRequest);
  });
}

export function cancelHrWfhRequest(id: string): Promise<true | ErrorInfo> {
  return api.cancelHrWfhRequest(id).then((response) => {
    if (response.ok) return true;
    return fail(response, api.cancelHrWfhRequest);
  });
}

export function getHrApprovals(): Promise<HrApprovalsUiModel | ErrorInfo> {
  return api.getHrApprovals().then((response) => {
    if (response.ok) return HrMapper.mapApprovals(unwrap<HrApprovalsModel>(response.data));
    return fail(response, api.getHrApprovals);
  });
}

export function decideHrApproval(
  kind: HrApprovalKind,
  id: string,
  decision: HrApprovalDecision,
  reason?: string
): Promise<true | ErrorInfo> {
  return api.decideHrApproval(kind, id, decision, reason).then((response) => {
    if (response.ok) return true;
    return fail(response, api.decideHrApproval);
  });
}

export function getHrAttendance(): Promise<HrAttendanceUiModel | ErrorInfo> {
  return api.getHrAttendanceStatus().then((response) => {
    if (response.ok && response.data) {
      return HrMapper.mapAttendance(unwrap<HrAttendanceStatusModel>(response.data));
    }
    return fail(response, api.getHrAttendanceStatus);
  });
}

export function toggleHrAttendance(
  action: 'clock-in' | 'clock-out'
): Promise<HrAttendanceUiModel | ErrorInfo> {
  return api.clockHrAttendance(action).then((response) => {
    if (response.ok && response.data) {
      return HrMapper.mapAttendance(unwrap<HrAttendanceStatusModel>(response.data));
    }
    return fail(response, api.clockHrAttendance);
  });
}
